import { FC, FocusEvent, useEffect, useState } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { isEmpty, isNil } from 'ramda';

import { DataRoomObjectFragment } from '../../graphql/generated';
import { useSearchPageQueryParams } from '../../hooks/use-search-page-query-params/use-search-page-query-params';
import { DatePicker, InputWithLabel } from '../date-picker/date-picker';
import { SearchInput } from '../search-input/search-input';
import { convertToDate } from '../search-results-container/date-utils/date-utils';
import { TreatyFilter } from '../treaty-filter/treaty-filter';

export type DateFilter = Date | null | undefined;

type SearchToolbarProps = {
  count?: number;
  isLoading: boolean;
  treaties: DataRoomObjectFragment[];
  onSearch: (
    searchTerm: string,
    treaties: string[],
    from: DateFilter,
    to: DateFilter
  ) => void;
};

export const initialiseTreatyFilter = (
  initial: string[],
  fallback: DataRoomObjectFragment[]
) => (!isEmpty(initial) ? initial : fallback.map((treaty) => treaty.cedant));

export const SearchToolbar: FC<SearchToolbarProps> = ({
  count,
  treaties,
  onSearch,
  isLoading,
}) => {
  const { params } = useSearchPageQueryParams();
  const [searchValue, setSearchValue] = useState<string>(params.searchTerm);
  const [treatyFilter, setTreatyFilter] = useState<string[]>(
    initialiseTreatyFilter(params.treaties, treaties)
  );
  const [fromDate, setFromDate] = useState<DateFilter>(
    convertToDate(params.from, { startOfDay: true })
  );
  const [toDate, setToDate] = useState<DateFilter>(
    convertToDate(params.to, { endOfDay: true })
  );

  useEffect(() => {
    setSearchValue(params.searchTerm);
  }, [params.searchTerm]);

  const handleSearchValueChange = (value: string) => setSearchValue(value);

  const handleTreatyFilterChange = (treaty: string) => {
    const updatedtreatyFilter = treatyFilter.includes(treaty)
      ? [...treatyFilter].filter((x) => x !== treaty)
      : [...treatyFilter, treaty];

    setTreatyFilter(updatedtreatyFilter);
  };

  const handleSelectAllTreaties = () =>
    setTreatyFilter(initialiseTreatyFilter([], treaties));
  const handleClearTreatyFilter = () => setTreatyFilter([]);

  // Enables typing a date on the datepicker
  const handleDatePickerChangeRaw = (
    event: FocusEvent<HTMLInputElement, Element>,
    setStateFn: (date: Date) => void
  ) => {
    const dayjsDate = dayjs(event.target.value);
    if (dayjsDate.isValid()) {
      setStateFn(dayjsDate.toDate());
    }
  };

  const handleSearch = () => {
    if (isEmpty(searchValue)) return;

    const selectedTreaties = initialiseTreatyFilter(treatyFilter, treaties);

    setTreatyFilter(selectedTreaties);

    onSearch(searchValue, selectedTreaties, fromDate, toDate);
  };

  const today = new Date();

  return (
    <Flex justifyContent="space-between" flexWrap="wrap" gridRowGap="4">
      <Flex
        gridGap="4"
        flexWrap="wrap"
        alignItems="center"
        width={{ xs: '40', sm: '96' }}
      >
        <SearchInput
          onSearch={handleSearch}
          searchValue={searchValue}
          onChange={handleSearchValueChange}
        />
        <TreatyFilter
          treaties={treaties}
          selectedTreaties={treatyFilter}
          onChange={handleTreatyFilterChange}
          onSelectAll={handleSelectAllTreaties}
          onDeselectAll={handleClearTreatyFilter}
        />
        <Flex maxW="210px">
          <DatePicker
            selected={fromDate}
            onChange={setFromDate}
            onChangeRaw={(event) => {
              handleDatePickerChangeRaw(event, setFromDate);
            }}
            customInput={
              <InputWithLabel label="From" data-testid="from-input" />
            }
            maxDate={toDate || today}
          />
        </Flex>
        <Flex maxW="200px">
          <DatePicker
            selected={toDate}
            onChange={setToDate}
            onChangeRaw={(event) => {
              handleDatePickerChangeRaw(event, setToDate);
            }}
            customInput={<InputWithLabel label="To" data-testid="to-input" />}
            minDate={fromDate}
            maxDate={today}
          />
        </Flex>
        <Button
          isLoading={isLoading}
          onClick={handleSearch}
          data-testid="search-btn"
          colorScheme="blue"
        >
          Search
        </Button>
      </Flex>
      {!isNil(count) && (
        <Text
          fontWeight="500"
          minWidth="24rem"
          data-testid="result-count"
          textAlign={{ sm: 'left', md: 'right' }}
        >
          Showing {count} results
        </Text>
      )}
    </Flex>
  );
};
